import { type Editor, type Range } from '@tiptap/core';
import {
  exitSuggestion,
  type SuggestionKeyDownProps,
  type SuggestionOptions,
  type SuggestionProps,
} from '@tiptap/suggestion';
import { useCallback, useMemo, useRef, useState } from 'react';

import { useGetSkills } from '../../../../api/skills/useGetSkills';
import { useChatComposerStore } from '../../../../store/useChatComposerStore';
import { type Skill } from '../../skills';
import { skillCommandInsertContent } from './SkillCommandNode';
import { type SkillSuggestionPopupRef } from './SkillSuggestionPopup';
import { SLASH_COMMAND_SUGGESTION_PLUGIN_KEY } from './SlashCommandSuggestion';

export const filterSkills = (skills: Skill[], query: string): Skill[] => {
  if (!query) {
    return skills;
  }

  const lower = query.toLowerCase();

  return skills.filter(
    (s) =>
      s.name.toLowerCase().includes(lower) ||
      s.description.toLowerCase().includes(lower) ||
      s.slashCommand.toLowerCase().includes(lower),
  );
};

export type SkillSuggestionState = {
  command: ((item: Skill) => void) | null;
  isOpen: boolean;
  items: Skill[];
};

const INITIAL_STATE: SkillSuggestionState = {
  command: null,
  isOpen: false,
  items: [],
};

/**
 * TipTap Suggestion dispatches an interim update per keystroke with `loading: true` and empty items,
 * then a second one once `items()` resolves. Rendering the interim one empties the list, which
 * unmounts the popover and remounts it unpositioned, so it flashes off screen on every keystroke.
 * While loading, keep the previous items and take only the fresh `command` (its range is current).
 */
export const getNextSkillSuggestionState = (
  previous: SkillSuggestionState,
  props: Pick<SuggestionProps<Skill, Skill>, 'command' | 'items' | 'loading'>,
): SkillSuggestionState => ({
  command: props.command,
  isOpen: true,
  items: props.loading ? previous.items : props.items,
});

export const useSkillSuggestionState = (enabled: boolean, workspaceId: string | undefined) => {
  const [state, setState] = useState<SkillSuggestionState>(INITIAL_STATE);
  const popupRef = useRef<SkillSuggestionPopupRef>(null);
  // No initializer: a literal one makes Biome treat `.current` as permanently null, though
  // onStart/onUpdate assign the editor. rules/biome-warning-reactions.md
  const editorRef = useRef<Editor | null>();
  const { data: skills = [] } = useGetSkills({ workspaceId }, { enabled });
  const skillsRef = useRef(skills);
  skillsRef.current = skills;

  /** Canvas "Try it" and other callers only have a slash command string — look up the skill here. */
  const addPendingSkillForSlashCommand = useCallback((slashCommand: string) => {
    const skill = skillsRef.current.find((entry) => entry.slashCommand === slashCommand);

    if (skill) {
      useChatComposerStore.getState().addPendingSkillInstructions(skill);
    }
  }, []);

  const dismissFromPortal = useCallback(() => {
    const ed = editorRef.current;

    if (ed) {
      exitSuggestion(ed.view, SLASH_COMMAND_SUGGESTION_PLUGIN_KEY);
    }
  }, []);

  const suggestionOptions: Omit<SuggestionOptions<Skill, Skill>, 'editor'> = useMemo(
    () => ({
      command: ({ editor, range, props: skill }: { editor: Editor; range: Range; props: Skill }) => {
        // Keep focus() out of the chain — view.focus() can sync-dispatch a selection tr and trip "Applying a mismatched transaction" on .run().
        editor.commands.focus();
        editor.chain().deleteRange(range).insertContent(skillCommandInsertContent(skill.slashCommand)).run();
        // Atoms do not leave `/command ` text for appendTransaction — add pending here (typed path uses onSkillMatch).
        useChatComposerStore.getState().addPendingSkillInstructions(skill);
      },

      items: ({ query }: { query: string }) => filterSkills(skillsRef.current, query),

      render: () => ({
        onExit: () => {
          editorRef.current = null;
          setState(INITIAL_STATE);
          useChatComposerStore.getState().setChatSlashCommandSuggestionOpen(false);
        },

        onKeyDown: ({ event }: SuggestionKeyDownProps) => {
          return popupRef.current?.onKeyDown(event) ?? false;
        },

        onStart: (props: SuggestionProps<Skill, Skill>) => {
          editorRef.current = props.editor;
          useChatComposerStore.getState().setChatSlashCommandSuggestionOpen(true);
          setState((previous) => getNextSkillSuggestionState(previous, props));
        },

        onUpdate: (props: SuggestionProps<Skill, Skill>) => {
          editorRef.current = props.editor;
          useChatComposerStore.getState().setChatSlashCommandSuggestionOpen(true);
          setState((previous) => getNextSkillSuggestionState(previous, props));
        },
      }),
    }),
    [],
  );

  return { addPendingSkillForSlashCommand, dismissFromPortal, popupRef, skills, skillsRef, state, suggestionOptions };
};
